/**
 * Bounds for the sign-in path's requests.
 *
 * 2026-10-10: a sign-in that "hangs" was traced to the client, not the server
 * — the server answers a correct password in ~1 s (bcrypt cost 12) and a wrong
 * one in ~0.8 s, but neither the login POST nor the session probe that follows
 * it had a timeout, so one stalled request left "Signing in…" on the button
 * for ever with nothing said. Each of those requests now carries a signal that
 * aborts it, and the page says what happened.
 *
 * The signal is built on setTimeout + AbortController rather than
 * `AbortSignal.timeout`, so it exists in every runtime the app and its tests
 * run in and fake timers can drive it. The abort reason is a DOMException
 * named 'TimeoutError', the name `AbortSignal.timeout` uses.
 */

/** The login POST: bcrypt at cost 12 plus a cold edge start, with room to spare. */
export const LOGIN_TIMEOUT_MS = 15_000;

/** GET /api/auth/me and /api/data/settings: two KV reads. */
export const SESSION_PROBE_TIMEOUT_MS = 10_000;

export function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort(new DOMException(`no answer within ${ms / 1000} s`, 'TimeoutError'));
  }, ms);
  return controller.signal;
}

/** True for the abort a `timeoutSignal` raises. */
export function isTimeout(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'TimeoutError';
}

/**
 * The one message for a request on this path that did not complete: a
 * timeout says how long we waited, a network failure says the server was not
 * reached, anything else keeps its own message.
 */
export function requestFailureMessage(e: unknown, ms: number, fallback: string): string {
  if (isTimeout(e)) {
    return `The server did not answer within ${ms / 1000} s. Check your connection and try again.`;
  }
  if (e instanceof TypeError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}
